import { z } from "zod";
import { isoDateTimeString, paginationQuerySchema, uuidSchema } from "./common";
import {
  availableFromSchema,
  employmentTypeSchema,
  genderSchema,
  workerPositionSchema,
} from "./users";
import { applicantCvSchema } from "./applications";
import { kycStatusSchema } from "./kyc";

// ----------------------------------------------------------------------------
// Talent sourcing — "Tìm ứng viên" (docs/01-product.md). Employer browses open-to-work
// workers in a deck and invites them. Identity is masked pre-commit, exactly
// like the applicant deck; contact only unlocks at COMMITTED on the resulting
// invite.
// ----------------------------------------------------------------------------

/** Compact work-experience entry shown on the deck card (the F&B hiring signal
 *  the employer scans first). Full detail lives in the CV modal. */
export const talentCardExperienceSchema = z.object({
  position: workerPositionSchema,
  placeName: z.string().nullable(),
  startYear: z.number().int().nullable(),
  endYear: z.number().int().nullable(),
  current: z.boolean(),
});
export type TalentCardExperience = z.infer<typeof talentCardExperienceSchema>;

/** One candidate card in the "Tìm ứng viên" deck. */
export const talentCardSchema = z.object({
  workerUserId: uuidSchema,
  maskedName: z.string(),
  avatarUrl: z.string().nullable(),
  age: z.number().int().nullable(),
  gender: genderSchema.nullable(),
  currentDistrict: z.string().nullable(),
  distanceKm: z.number().nullable(),
  desiredPositions: z.array(workerPositionSchema),
  employmentTypes: z.array(employmentTypeSchema),
  availableFrom: availableFromSchema.nullable(),
  bioShort: z.string().nullable(),
  skills: z.array(z.string()),
  advantages: z.array(z.string()),
  // A few recent roles, surfaced on the card itself (employer mental model:
  // experience + bio are the primary signals). Capped server-side.
  experiences: z.array(talentCardExperienceSchema),
  experienceCount: z.number().int(),
  // Total months of work experience (summed across all roles) so the employer
  // sees seniority at a glance, e.g. "2 năm 3 tháng".
  totalExperienceMonths: z.number().int(),
  hasMotorbike: z.boolean(),
  kycStatus: kycStatusSchema,
  kycVerified: z.boolean(),
  ratingAvg: z.number().nullable(),
  ratingCount: z.number().int(),
  completionPct: z.number().int(),
  openToWorkUpdatedAt: isoDateTimeString.nullable(),
});
export type TalentCard = z.infer<typeof talentCardSchema>;

/** Filters for the talent feed. Cursor + limit come from paginationQuerySchema. */
export const talentFeedQuerySchema = paginationQuerySchema.extend({
  position: workerPositionSchema.optional(),
  district: z.string().optional(),
  employmentType: employmentTypeSchema.optional(),
  kycOnly: z.coerce.boolean().optional(),
  hasMotorbike: z.coerce.boolean().optional(),
});
export type TalentFeedQuery = z.infer<typeof talentFeedQuerySchema>;

export const talentFeedResponseSchema = z.object({
  items: z.array(talentCardSchema),
  nextCursor: z.string().nullable(),
});
export type TalentFeedResponse = z.infer<typeof talentFeedResponseSchema>;

/** Full CV detail for a candidate in the deck — reuses the applicant CV shape
 *  so the employer sees the same profile view as for an applicant (docs/01-product.md). */
export const talentProfileResponseSchema = z.object({
  worker: applicantCvSchema,
});
export type TalentProfileResponse = z.infer<typeof talentProfileResponseSchema>;

/** Employer sources an open-to-work worker into a specific OPEN posting. */
export const createSourcedInviteRequestSchema = z.object({
  postingId: uuidSchema,
  orgAddressId: uuidSchema,
  slotIds: z.array(uuidSchema).min(1).max(10),
});
export type CreateSourcedInviteRequest = z.infer<
  typeof createSourcedInviteRequestSchema
>;

/** Worker blocks an org from sourcing/inviting them ("Ẩn quán này", docs/01-product.md §11). */
export const blockOrgRequestSchema = z.object({
  orgId: uuidSchema,
});
export type BlockOrgRequest = z.infer<typeof blockOrgRequestSchema>;

export const blockOrgResponseSchema = z.object({ ok: z.boolean() });
export type BlockOrgResponse = z.infer<typeof blockOrgResponseSchema>;
